import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { singletonHook } from 'react-singleton-hook';

import { IUserSupplementImageSettings } from '@interfaces/supplements';
import { handleGetUserSupplementSettings } from '@store/features/supplementSettings/actions';
import {
  userSupplementSettingsErrorSelector,
  userSupplementSettingsFormatsSelector,
  userSupplementSettingsIsLoadingSelector,
  userSupplementSettingsIsReadySelector,
  userSupplementSettingsMaxFileSizeSelector,
} from '@store/features/supplementSettings/selectors';
import useDispatch from '@store/hooks/useDispatch';

import useFetchLookupData from './useFetchLookupData';

const useSupplementsImageSettings = (): IUserSupplementImageSettings => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const allowedFileExtensionsInquiryUpload = useSelector(userSupplementSettingsFormatsSelector);
  const maxFileSizeInquiryUploadMb = useSelector(userSupplementSettingsMaxFileSizeSelector);
  const isLoading = useSelector(userSupplementSettingsIsLoadingSelector);
  const isReady = useSelector(userSupplementSettingsIsReadySelector);
  const error = useSelector(userSupplementSettingsErrorSelector);

  const handleFetch = useCallback(() => {
    dispatch(handleGetUserSupplementSettings());
  }, [dispatch]);

  useFetchLookupData({
    isReady,
    isLoading,
    error: error && t('errors.supplementsImagesSettingsError'),
    handleFetch,
  });

  return {
    allowedFileExtensionsInquiryUpload,
    maxFileSizeInquiryUploadMb,
  };
};

export default singletonHook(
  {
    allowedFileExtensionsInquiryUpload: [],
    maxFileSizeInquiryUploadMb: 0,
  },
  useSupplementsImageSettings,
);
